<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elavation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to previous page" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="finance" pageId="cash-advances-other-employee" color="white"></help-link>
                    <v-btn v-if="hasAddRights" fab small dark color="primary" title="Create Cash Advances for any employee" @click="showAdd">
                        <v-icon dark>add</v-icon>
                    </v-btn>
                </v-toolbar>
            </v-card>
            <v-card class="elavation-1 mt-5">
                <v-toolbar flat height="45px">
                    <div :class="isMobile ? 'wp-60 mb-10' : 'wp-30 mb-10'">
                        <v-text-field v-model="search" append-icon="search" label="Search name, type, status" single-line hide-details></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                    <div>
                        <v-switch
                            v-model="viewDeleted"
                            label="View Deleted"
                            color="primary"
                            hide-details                        
                        ></v-switch> 
                    </div>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? [] : headers"
                    :items="cash_advances_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >
                    <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="headers.length" class="pa-1">
                                <v-card class="elevation-1" @click="viewDetails(props.item.cashAdvancesId)">
                                    <v-toolbar flat height="42">
                                        <v-chip @click="viewUserProfile(props.item, $store.state.userId)" class="user-chip-container">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                            </v-avatar>
                                            {{props.item.name}} 
                                        </v-chip>
                                        <v-spacer></v-spacer>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="section-layout">
                                        <v-flex xs12 class="text-xs-left mt-5 mb-2">
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Type</div>
                                                    <div>{{props.item.cashAdvanceType}}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Payment Start Date</div>
                                                    <div>{{props.item.paymentStart | moment('MMM DD, YYYY')}}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Total Amount</div>
                                                    <div>₱{{numFormat.formatNumber(props.item.totalAmount)}}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Total Paid</div>
                                                    <div>{{numFormat.formatNumber(getTotalPaid(props.item.paymentSchedule))}}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Status</div>
                                                    <div :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else @click="viewDetails(props.item.cashAdvancesId)" class="clickable">
                            <td>
                                <v-chip @click="viewUserProfile(props.item, $store.state.userId)" class="user-chip-container">
                                    <v-avatar>
                                        <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                    </v-avatar>
                                    {{props.item.name}} 
                                </v-chip>
                            </td>
                            <td>{{props.item.cashAdvanceType}}</td>
                            <td>{{props.item.paymentStart | moment('MMM DD, YYYY')}}</td>
                            <td>₱{{numFormat.formatNumber(props.item.totalAmount)}}</td>
                            <td>{{numFormat.formatNumber(getTotalPaid(props.item.paymentSchedule))}}</td>
                            <td>
                                <b :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</b>
                            </td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>
        </v-flex>

        <v-dialog v-model="dialogAddEdit" width="700" persistent>   
            <add-edit ref="addEditRef" :dialogAddEdit.sync="dialogAddEdit" @send="viewDetails"></add-edit>
        </v-dialog>
    </v-layout> 
</template>

<script>

import cashadvancesApi from '@/api/cashadvances'
import commonfn from '@/common/functions'
import numFormat from '@/common/num_format'
import pageKeys from '@/common/pagekeys'
import awsS3Helper from '@/helpers/aws-s3'
import userPrefs from '@/helpers/user-prefs'
import rowsPerPageItems from '@/constants/rows-per-page'
import { hasAddRights } from '@/helpers/access-permissions'

export default {     
    name: 'CashAdvances',
    metaInfo: {
        title: 'Cash Advances',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'View, create Cash Advances' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'add-edit': () => import('@/components/CashAdvances/AddEditCashAdvances')
    },
    data () {
        const routeName = this.$route.name
        const savedSort = userPrefs.getSort(routeName)

        return {
            commonfn,
            numFormat,
            awsS3Helper,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: userPrefs.getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            search: userPrefs.getSearchKeyword(routeName),
            error: false,
            errorMessage: '',
            loading: false,
            dialogAddEdit: false,
            headers: [
                {text: 'Name',align: 'left', value: 'name', width: '20%', sortable:true},
                {text: 'Type',align: 'left', value: 'cashAdvanceType', width: '20%', sortable:true},
                {text: 'Payment Start Date', align: 'left',value: 'paymentStart',  width: '15%', sortable:true},
                {text: 'Total Amount', align: 'left',value: 'totalAmount',  width: '15%', sortable:true},
                {text: 'Total Paid', align: 'left',value: 'paymentSchedule',  width: '15%', sortable:true},
                {text: 'Status', align: 'left', value:'status',  width: '15%', sortable:true},
            ],
            cash_advances: [],
            viewDeleted: false,
        }
    },
    methods: {
        showAdd() {
            const data = {
                isRequestForOtherUser: true,
                pageKey: pageKeys.cash_advances
            }
            this.$refs.addEditRef.setAdd(data)
            this.dialogAddEdit = true
        },
        getTotalPaid(data){
            var paidSchedule = data.filter(x => {
                return x.paidOn != null && x.status == commonfn.getStatusCode('Paid')
            })
            if (paidSchedule.length>0) return paidSchedule.map(x => x.amountDue).reduce((prev,next) => prev+next)
            else return 0
        },
        viewUserProfile(data, loggedInUserId) {
            if (data.userId == loggedInUserId) this.$router.push(`/myprofile`)
            else this.$router.push(`/hr/employees/${data.userId}`)
        },
        viewDetails(cashAdvancesId) {
            this.$router.push(`/finance/cashadvances/${cashAdvancesId}`)
        },
        async getCashAdvances(show_loading) {
            try {
                this.loading = show_loading
                const response = await cashadvancesApi.getSummary()
                if (response.status == 200) {
                    this.cash_advances = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        }
    },
    created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Cash Advances'
        })

        this.getCashAdvances(true)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            userPrefs.updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            userPrefs.updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            userPrefs.updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        },
        search(newVal) {
            userPrefs.updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        cash_advances_filtered() {
            var temp = this.cash_advances.filter(x => x.deleted == this.viewDeleted)
            if (this.search) {
                temp = temp.filter(item => {
                    const searchLowered = this.search.toLowerCase()
                    const nameMatch = item.name.toLowerCase().includes(searchLowered)
                    const typeMatch = item.cashAdvanceType.toLowerCase().includes(searchLowered)
                    const statusMatch = commonfn.getStatusDescription(item.status).toLowerCase().includes(searchLowered) 
                    return nameMatch || typeMatch || statusMatch
                })
            }
            return temp
        },
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
        isMobile() {
            return window.innerWidth < 600
        },
    }
}
</script>
<style scoped>
.section-layout {
    padding:0px 23px 0px 23px;
}
</style>